import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { DispatchCellColors, DispatchCellColorSettings } from 'src/pages/LongHaul/resolveCellColor';

export const DISPATCH_CELL_COLORS_PATH = '/api/v5/dispatch_cell_colors';

/**
 * 会社ごとに 1 行なので、キーに会社を持たない。会社を切り替えると API 側の
 * セッション（SwitchCompanyController）ごと別の会社になり、画面も作り直される。
 */
export const dispatchCellColorsQueryKey = () => ['useQueryDispatchCellColors'] as const;

/**
 * 配車画面のセル色分け設定（log_web_issues#338 C2）。未登録の会社にも既定値が返る。
 * 会社の設定に、エリアと所属県の対応表（`areas`・全社共通のマスタ）が付いてくる。
 */
export const useQueryDispatchCellColors = (enabled: boolean) => useQuery({
  queryKey: dispatchCellColorsQueryKey(),
  queryFn: async (): Promise<DispatchCellColors> => {
    const response = await axios.get<DispatchCellColors>(DISPATCH_CELL_COLORS_PATH);
    return response.data;
  },
  enabled,
  staleTime: Infinity,
});

/**
 * セル色分け設定の保存。**設定全体の置き換え**なので、呼び出し側は常に 4 つのキーを
 * 揃えて渡す（省略したキーは API 側で既定値に戻る）。
 *
 * 送るのは会社が書き換える 4 キーだけ。レスポンスには `areas` も載るので、キャッシュへ
 * 入れ替えても対応表が消えない。
 *
 * レスポンスはサーバ確定値そのものなので、invalidate ではなくキャッシュへ直接入れる。
 * Why not invalidate: 取り直しの 1 往復のあいだ、閉じたダイアログで選んだ色が
 * バーに出ないまま残る（色を選び直したのか反映が遅いのか画面から区別できない）。
 */
export const useMutationDispatchCellColors = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: DispatchCellColorSettings): Promise<DispatchCellColors> => {
      const { data } = await axios.put<DispatchCellColors>(DISPATCH_CELL_COLORS_PATH, settings);
      return data;
    },
    onSuccess: (data) => { queryClient.setQueryData(dispatchCellColorsQueryKey(), data); },
  });
};
